import { formatDistanceToNow as dateFnsDistanceToNow, type Locale } from 'date-fns';
// Per locale importeren, niet via 'date-fns/locale': die barrel is ESM-only, en Jest
// van react-scripts transformeert node_modules niet. Scheelt bovendien bundelgrootte.
import { enGB } from 'date-fns/locale/en-GB';
import { nl } from 'date-fns/locale/nl';
import { parseLocalDateTime } from './timeUtils';

/**
 * Eén plek voor datumopmaak, zodat schermen niet elk hun eigen 'nl-NL' meesturen.
 *
 * Bewust géén expliciete `timeZone`: de app draait op Europe/Amsterdam en de bestaande
 * schermen tonen de kloktijd van de browser. Een timeZone toevoegen zou de getoonde
 * tijden veranderen voor iedereen buiten Nederland, en dat is een aparte beslissing.
 */

/**
 * Engelstalige gebruikers zitten in Nederland (expat-ouders en -coaches), dus houden we
 * de Nederlandse conventies aan: dag vóór maand en een 24-uursklok. 'en-GB' geeft precies
 * dat en is in ICU identiek aan 'en-NL'. Let op: het kale 'en' valt terug op Amerikaanse
 * opmaak (maand vóór dag, AM/PM), dus we mappen altijd expliciet.
 */
const INTL_LOCALES: Record<string, string> = {
  nl: 'nl-NL',
  en: 'en-GB',
};

const DATE_FNS_LOCALES: Record<string, Locale> = {
  nl,
  en: enGB,
};

export const DEFAULT_INTL_LOCALE = INTL_LOCALES.nl;

/** De opmaakvarianten die in de app voorkomen. Namen beschrijven de vorm, niet de plek. */
export const DATE_FORMATS = {
  /** 10-08-2026 */
  date: { day: '2-digit', month: '2-digit', year: 'numeric' },
  /** 10-08-26 */
  dateShortYear: { day: '2-digit', month: '2-digit', year: '2-digit' },
  /** 10-08 */
  dayMonth: { day: '2-digit', month: '2-digit' },
  /** 10-08-2026, 14:05 */
  dateTime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  /** 10-08, 14:05 */
  dayMonthTime: { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' },
  /** maandag 10-08-2026 */
  weekdayDate: { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' },
  /** maandag 10-08-2026, 14:05 */
  weekdayDateTime: {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  /** ma 10 aug 2026 */
  abbreviated: { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
  /** za 19 sep */
  weekdayDayMonth: { weekday: 'short', day: 'numeric', month: 'short' },
  /** 10 augustus 2026 om 14:05 */
  longDateTime: { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
  /** 14:05 */
  time: { hour: '2-digit', minute: '2-digit' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DateFormatName = keyof typeof DATE_FORMATS;

/** Wat een scherm zoal doorgeeft: de ruwe API-string, een Date, of niets. */
export type DateInput = string | Date | null | undefined;

/** i18next levert 'nl' of 'en'; een regiovariant als 'en-GB' wordt teruggebracht. */
export const toIntlLocale = (language: string): string =>
  INTL_LOCALES[language?.split('-')[0]] ?? DEFAULT_INTL_LOCALE;

export const toDateFnsLocale = (language: string): Locale =>
  DATE_FNS_LOCALES[language?.split('-')[0]] ?? nl;

// Een Intl.DateTimeFormat opbouwen is relatief duur en deze formatters draaien in tabellen
// met tientallen rijen, dus hergebruiken we ze per combinatie van taal en opmaak.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (locale: string, format: DateFormatName): Intl.DateTimeFormat => {
  const cacheKey = `${locale}|${format}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, DATE_FORMATS[format]);
    formatterCache.set(cacheKey, formatter);
  }
  return formatter;
};

const toDate = (value: DateInput): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : parseLocalDateTime(value);
  // Intl gooit een RangeError op een ongeldige datum, terwijl toLocaleDateString vroeger
  // gewoon 'Invalid Date' teruggaf. Zonder deze controle klapt een scherm om op rommeldata.
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Opmaak met een expliciete taal. Componenten gebruiken `useDateFormat()`; deze variant is
 * voor code buiten React (helpers, tests) die de taal zelf al kent.
 */
export const formatDateIn = (
  language: string,
  value: DateInput,
  format: DateFormatName = 'date',
  fallback = '',
): string => {
  const date = toDate(value);
  if (!date) return fallback;
  return getFormatter(toIntlLocale(language), format).format(date);
};

export const formatDistanceToNowIn = (language: string, value: DateInput, fallback = ''): string => {
  const date = toDate(value);
  if (!date) return fallback;
  return dateFnsDistanceToNow(date, { addSuffix: true, locale: toDateFnsLocale(language) });
};

export interface DateFormatters {
  /** Opmaak van een datum/tijd in de actieve taal. */
  formatDate: (value: DateInput, format?: DateFormatName, fallback?: string) => string;
  /** Relatieve tijd ('2 uur geleden' / '2 hours ago'). */
  formatDistanceToNow: (value: DateInput, fallback?: string) => string;
  /** Voor de zeldzame plek die zelf een Intl-instantie nodig heeft. */
  intlLocale: string;
  dateFnsLocale: Locale;
}

export const createDateFormatters = (language: string): DateFormatters => ({
  formatDate: (value, format, fallback) => formatDateIn(language, value, format, fallback),
  formatDistanceToNow: (value, fallback) => formatDistanceToNowIn(language, value, fallback),
  intlLocale: toIntlLocale(language),
  dateFnsLocale: toDateFnsLocale(language),
});
